import qs from 'query-string';
import { useEffect } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  Container,
  Form,
  Grid,
  Input,
  Segment,
} from 'semantic-ui-react';

import { purgeMessages } from '@plone/volto/actions/messages/messages';
import {
  logout,
  resetLoginRequest,
} from '@plone/volto/actions/userSession/userSession';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';
import aheadSVG from '@plone/volto/icons/ahead.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import { toast } from 'react-toastify';
import { loginCode, sendCode } from '../../actions/user/user';

const messages = defineMessages({
  login: {
    id: 'Log in',
    defaultMessage: 'Log in',
  },
  loginName: {
    id: 'Login Name',
    defaultMessage: 'Login Name',
  },
  Login: {
    id: 'Login',
    defaultMessage: 'Login',
  },
  password: {
    id: 'Password',
    defaultMessage: 'Password',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  loginFailed: {
    id: 'Login Failed',
    defaultMessage: 'Login Failed',
  },
  loginCodeFailedContent: {
    id: 'login-code-failed-content',
    defaultMessage: 'Code not valid. Please check that the code is correct',
  },
  dni: {
    id: 'DNI',
    defaultMessage: 'DNI',
  },
  code: {
    id: 'Code',
    defaultMessage: 'Login code',
  },
  codeSentSuccess: {
    id: 'code-sent-success',
    defaultMessage: 'Code sent successfully',
  },
  codeSentSuccessContent: {
    id: 'code-sent-success-content',
    defaultMessage:
      'We have sent a code to your email and to your mobile phone. Insert it in the field below to login.',
  },
  codeSentFailed: {
    id: 'code-sent-failed',
    defaultMessage: 'Error sending code',
  },
  codeSentFailedContent: {
    id: 'code-sent-failed-content',
    defaultMessage:
      'The data entered is not valid. Please check that the DNI is correct.',
  },
});

const Login = (props) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const token = useSelector(
    (state: any) => state.userSession.token,
    shallowEqual,
  );

  const errorCode = useSelector((state: any) => state.userSession?.code?.error);
  const errorLogin = useSelector(
    (state: any) => state.userSession?.login?.error,
  );

  const loadingCode = useSelector(
    (state: any) => state.userSession?.code?.loading,
  );
  const loadingLogin = useSelector(
    (state: any) => state.userSession?.login?.loading,
  );
  const codeSent = useSelector((state: any) => state.userSession?.code?.loaded);

  const returnUrl =
    qs.parse(props.location?.search ?? location.search).return_url ||
    location.pathname.replace(/\/[^/]*\/?$/, '') ||
    '/';
  const previousToken = usePrevious(token);

  useEffect(() => {
    if (location?.state?.isLogout) {
      // Execute a true Logout action
      // This is needed to cover the use case of being logged in in
      // another backend (eg. in development), having a token for
      // localhost and try to use it, the login route has to know that
      // it's the same as it comes from a logout
      // See also Unauthorized.jsx
      dispatch(logout());
      dispatch(purgeMessages());
      // Reset the location state
      history.push(`${location.pathname}${location.search}`);
    } else if (token && token !== previousToken) {
      // We just did a true login action
      toast.dismiss();
      console.log('returnUrl', returnUrl);
      history.push(returnUrl || '/');
      // window.location.href = returnUrl || '/';
    }
    if (errorCode || errorLogin) {
      if (errorCode && !toast.isActive('sendCodeFailed')) {
        toast.error(
          <Toast
            error
            title={intl.formatMessage(messages.codeSentFailed)}
            content={intl.formatMessage(messages.codeSentFailedContent)}
          />,
          { autoClose: false, toastId: 'sendCodeFailed' },
        );
      } else if (errorLogin && !toast.isActive('loginFailed')) {
        toast.error(
          <Toast
            error
            title={intl.formatMessage(messages.loginFailed)}
            content={intl.formatMessage(messages.loginCodeFailedContent)}
          />,
          { autoClose: false, toastId: 'loginFailed' },
        );
      }
    }

    if (
      codeSent &&
      !errorCode &&
      !errorLogin &&
      !loadingLogin &&
      !loadingCode &&
      !toast.isActive('codeSentSuccess') &&
      !token
    ) {
      toast.success(
        <Toast
          title={intl.formatMessage(messages.codeSentSuccess)}
          content={intl.formatMessage(messages.codeSentSuccessContent)}
        />,
        { autoClose: false, toastId: 'codeSentSuccess' },
      );
    }
    return () => {
      if (toast.isActive('loginFailed')) {
        toast.dismiss('loginFailed');
        dispatch(resetLoginRequest());
      }
    };
  }, [
    dispatch,
    token,
    errorCode,
    errorLogin,
    codeSent,
    intl,
    history,
    returnUrl,
    location.search,
    location.pathname,
    location?.state?.isLogout,
    previousToken,
  ]);

  const onLogin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const dni = formData.get('dni') as string;
    const code = formData.get('code') as string;
    toast.dismiss();
    if (code) {
      dispatch(loginCode(dni, code));
    } else {
      dispatch(sendCode(dni));
    }
  };

  return (
    <div id="page-code-login">
      <Helmet title={intl.formatMessage(messages.Login)} />
      <Container>
        <h1>
          <strong>
            <FormattedMessage
              id="porta-electe"
              defaultMessage="Portal de l'electe"
            />
          </strong>
        </h1>
        <p>
          <FormattedMessage
            id="porta-electe-description"
            defaultMessage="Tots els electes de Compromís Municipal, conjuntament amb els càrrecs orgànics del partit i el personal de suport al municipalisme, tenen accés al Portal de l'Electe."
          />
        </p>
      </Container>
      <Container text>
        <Form method="post" onSubmit={onLogin}>
          <Segment.Group raised>
            <Segment className="primary">
              <FormattedMessage id="Log In" defaultMessage="Login" />
            </Segment>
            <Segment secondary>
              <FormattedMessage
                id="Sign in to start session"
                defaultMessage="Sign in to start session"
              />
            </Segment>
            <Segment className="form">
              <Form.Field inline className="help">
                <Grid>
                  <Grid.Row stretched>
                    <Grid.Column width="4">
                      <div className="wrapper">
                        <label htmlFor="dni">
                          <FormattedMessage id="DNI" defaultMessage="DNI" />
                        </label>
                      </div>
                    </Grid.Column>
                    <Grid.Column width="8">
                      {/* eslint-disable jsx-a11y/no-autofocus */}
                      <Input
                        id="dni"
                        name="dni"
                        placeholder={intl.formatMessage(messages.dni)}
                        autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Form.Field>
              {codeSent && (
                <Form.Field inline className="help">
                  <Grid>
                    <Grid.Row stretched>
                      <Grid.Column width="4">
                        <div className="wrapper">
                          <label htmlFor="code">
                            {intl.formatMessage(messages.code)}
                          </label>
                        </div>
                      </Grid.Column>
                      <Grid.Column width="8">
                        {/* eslint-disable jsx-a11y/no-autofocus */}
                        <Input
                          id="code"
                          name="code"
                          placeholder={intl.formatMessage(messages.code)}
                          autoFocus
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Form.Field>
              )}
            </Segment>
            <Segment className="actions" clearing>
              <Button
                basic
                primary
                icon
                floated="right"
                type="submit"
                id="login-form-submit"
                aria-label={intl.formatMessage(messages.login)}
                title={intl.formatMessage(messages.login)}
                loading={loadingCode || loadingLogin}
              >
                <Icon className="circled" name={aheadSVG} size="30px" />
              </Button>

              <Button
                basic
                secondary
                icon
                floated="right"
                id="login-form-cancel"
                as={Link}
                to="/"
                aria-label={intl.formatMessage(messages.cancel)}
                title={intl.formatMessage(messages.cancel)}
              >
                <Icon className="circled" name={clearSVG} size="30px" />
              </Button>
            </Segment>
          </Segment.Group>
        </Form>
      </Container>
      <Container>
        <p>
          <FormattedMessage
            id="porta-electe-help-info"
            defaultMessage="Un cop validat el teu DNI/NIE, rebràs un correu electrònic amb un codi per accedir directament."
          />
        </p>
        <p>
          <FormattedMessage
            id="porta-electe-help-info-2"
            defaultMessage="Si no pots accedir i <cta>ets electe de Junts-CM</cta>, registra't a través del <e>formulari per constar a la nostra base de dades</e>."
            values={{
              e: (chunks) => (
                <UniversalLink href="/register" openLinkInNewTab={false}>
                  {chunks}
                </UniversalLink>
              ),
              cta: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </p>
        <p>
          <FormattedMessage
            id="porta-electe-help-info-3"
            defaultMessage="Si <cta>no ets electe</cta> però creus que hauries de tenir accés, envia'ns un correu a <e>politicamunicipal@junts.cat</e>"
            values={{
              e: (chunks) => (
                <a
                  target="_blank"
                  href="mailto:politicamunicipal@junts.cat"
                  rel="noreferrer"
                >
                  {chunks}
                </a>
              ),
              cta: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </p>
      </Container>
    </div>
  );
};

export default Login;
